import styled from 'styled-components'
import { Body } from '@shipt/design-system-typography'
import { TextButton } from '@shipt/design-system-buttons'
import { Row } from '@shipt/design-system-layouts'
import { screenSizes } from '@shipt/design-system-themes'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { useScrollWithThreshold } from '@/features/shared/components/Headers/useScrollWithThreshold'
import { DynamicStoreInfoDrawer } from '@/features/shop/components/Drawer/StoreInfoDrawer/DynamicStoreInfoDrawer'
import { getBoxShadow } from '@/features/shared/styles/effects'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { PromotionIcon } from '@shipt/design-system-icons'
import { useQueryCart } from '@/features/shop/services/Cart/queries'
import { formatPrice } from '@/features/shared/utils/formatter'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { useEffect } from 'react'
import { theme } from '@/features/shared/constants/shiptTheme'

export const C360NoMarkupBanner = () => {
  const { screenSm } = useMediaQuery()
  const { openDialog } = useHandleDialog()

  const isPastScrollThreshold = useScrollWithThreshold(90, {
    enabled: screenSm,
  })

  const { data: cart, isPending } = useQueryCart()
  const totalSavingsAmount = cart?.total_savings ?? 0
  const shouldShowOrderSavingsCopy = totalSavingsAmount > 0

  useEffect(() => {
    if (isPending) return

    trackElementViewed({
      link_name: 'nmu_banner',
      content: shouldShowOrderSavingsCopy ? 'Order Savings' : `You're shopping with no markups`,
      type: 'banner',
    })
  }, [isPending, shouldShowOrderSavingsCopy])

  const handleStorePricingClick = () => {
    trackElementClicked({
      link_name: 'nmu_learn_more',
      content: 'learn_more',
      type: 'banner',
      next_location: 'nmu_learn_more',
    })
    openDialog(DynamicStoreInfoDrawer, {
      defaultTab: 'storeInfo',
    })
  }

  if (isPending) return null

  return (
    <Wrapper isPastScrollThreshold={isPastScrollThreshold}>
      <Banner
        padding={{ base: ['sm', 0], md: ['lg', 0] }}
        align="center"
        justify={{ md: 'center' }}
        spacing={{ base: 'md', md: 'xxl' }}
      >
        <Row align="center" spacing="sm" justify="center">
          <PromotionIcon size={screenSm ? 'sm' : 'lg'} />
          <Body size={{ base: 'md', md: 'lg' }} align="left">
            {shouldShowOrderSavingsCopy ? (
              <Row as="span" spacing={{ base: 'xxs', md: 'sm' }}>
                Order savings: <strong>{formatPrice(totalSavingsAmount)}</strong>
              </Row>
            ) : (
              "You're shopping with no markups"
            )}
          </Body>
        </Row>
        <LearnMoreButton onClick={handleStorePricingClick} size={{ base: 'md', md: 'lg' }}>
          Learn more
        </LearnMoreButton>
      </Banner>
    </Wrapper>
  )
}

const Wrapper = styled(GridLayoutContainer)<{
  isPastScrollThreshold: boolean
}>`
  width: 100%;
  padding: 0;
  position: sticky;
  z-index: 4;
  max-width: 100%;
  background: ${theme.color.background.base.neutral.subtle_00};
  ${getBoxShadow('lift', 'light', false)};

  @media ${screenSizes.max_mobile} {
    transition: top 0.6s ease;
    top: ${({ isPastScrollThreshold }) => (isPastScrollThreshold ? '72px' : '0')};
    margin-top: -0.5rem;
  }

  @media ${screenSizes.tablet} {
    top: 124px;
  }

  @media ${screenSizes.smDesktop} {
    --content-max-width: 896px;
  }
`

const Banner = styled(Row)`
  position: relative;

  @media ${screenSizes.max_mobile} {
    justify-content: space-between;
  }
`

const LearnMoreButton = styled(TextButton)`
  line-height: inherit;
`
